import ModalContent from 'LuxKit/Modal/ModalContent'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import Image from 'components/Common/Image'
import TextLink from 'LuxKit/TextLink'
import React, { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { isBnblOrderWithoutEsimDates } from 'loyalty/lib/loyaltyEsimUtils'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import useCopyWithFeedback from 'hooks/useCopyWithFeedback'
import useOrder from 'hooks/Order/useOrder'
import useAccountEsimPromoCodes from 'loyalty/hooks/useAccountEsimPromoCodes'
import useOrderEsimPromoCodes from 'loyalty/hooks/useOrderEsimPromoCodes'
import LoyaltyEsimDetailsPromoCodeCard from './LoyaltyEsimDetailsPromoCodeCard'
import { ESIM_HUBBY_COMPATIBLE_PHONES_LINK, HUBBY_APP_STORE_URL, HUBBY_PLAY_STORE_URL, HUBBY_QR_IMAGE_ID } from 'loyalty/constants/loyaltyEsimConstants'

const CopiedFeedback = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  line-height: 1;
`

const RedeemSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  align-items: stretch;

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-start;
  }
`

const ContentStableMinHeight = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  min-height: ${rem(280)};
`

const StepsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  flex: 1 1 0%;
  min-width: 0;
`

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const StepItem = styled.div`
  display: flex;
  gap: ${rem(8)};
  align-items: flex-start;
`

const StepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(24)};
  height: ${rem(24)};
  min-width: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.secondary.weight.bold};
`

const QrColumn = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: ${rem(12)};
  align-items: center;
  justify-content: center;
  padding: ${rem(12)} ${rem(20)};
`

const QrImageWrap = styled.div`
  width: ${rem(104)};
  height: ${rem(104)};
  position: relative;
  overflow: hidden;
  flex-shrink: 0;

  picture, img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const AppBadgesRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${rem(8)};
  align-items: center;
  justify-content: center;
`

const SingleCodeItem = styled(ClickableListItem)`
  padding: ${rem(8)} ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const stepContentIndent = 32

const SingleCodeItemWrap = styled.div`
  margin-top: ${rem(4)};
  margin-inline-start: ${rem(stepContentIndent)};
`

const SingleCodeAvailableLaterBox = styled.div`
  margin-top: ${rem(4)};
  margin-inline-start: ${rem(stepContentIndent)};
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(12)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const InfoIconWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const SingleCodeSkeletonWrap = styled.div`
  margin-top: ${rem(4)};
  margin-inline-start: ${rem(stepContentIndent)};
`

const SingleCodeGeneratingBox = styled.div`
  margin-top: ${rem(4)};
  margin-inline-start: ${rem(stepContentIndent)};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${rem(8)};
  padding: ${rem(8)} ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${rem(4)};
`

const PromoCodesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(16)};
  align-items: start;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`

const PromoCodesSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  margin-inline-start: ${rem(-8)};
`

const NoCodesInfoBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const NoCodesInfoText = styled(BodyText)`
  flex: 1;
  min-width: 0;
`

export interface LoyaltyEsimDetailsContentProps {
  orderId?: string
  onViewOrder: (orderId: string) => void
  title?: React.ReactNode
}

function LoyaltyEsimDetailsContent({ orderId, onViewOrder, title }: LoyaltyEsimDetailsContentProps) {
  const intl = useIntl()
  const isOrderView = orderId != null
  const accountPromoCodes = useAccountEsimPromoCodes({ enabled: !isOrderView })
  const orderPromoCodes = useOrderEsimPromoCodes(orderId ?? '', { enabled: isOrderView })
  const promoCodes = isOrderView ? orderPromoCodes : accountPromoCodes

  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const [order] = useOrder(orderId)
  const { variation, codes, singleCode, orderPhase, isEligibleListReady } = promoCodes

  const { isCopied: showCopiedTooltip, copyText: copyToClipboard } = useCopyWithFeedback()

  const handleSingleCodeCopy = useCallback(() => {
    if (singleCode?.phase === 'success' && singleCode.code) {
      copyToClipboard(singleCode.code)
    }
  }, [singleCode, copyToClipboard])

  const hasOrderError = !!orderId && orderPhase === 'error' && variation === 'none'
  const hasSingleCode = variation === 'single' || orderPhase === 'booking_pending' || hasOrderError
  const hasMultipleCodes = variation === 'multiple' && codes.length > 0
  const hasNoCodes = variation === 'none' && !hasSingleCode
  const isBnblWithoutEsimDates = isBnblOrderWithoutEsimDates(order)
  const showOrderPromoCode = hasSingleCode && !isBnblWithoutEsimDates

  const copiedLabel = intl.formatMessage({
    id: '_yk6U9B2jYDeO5WBmcSv',
    defaultMessage: 'Copied',
    description: 'Text shown beside tick icon after copying eSIM promo code',
  })
  const introDescription = intl.formatMessage({
    id: '0FRgQEPHcvpkMhGTRk0G',
    defaultMessage: 'As a {loyaltyProgramName} member, you’ll receive Free Data Roaming on every eligible international booking — so you’re connected the moment you arrive.',
    description: 'Introductory description in the eSIM details modal for loyalty members',
  }, { loyaltyProgramName })
  const whatIsEsimHeading = intl.formatMessage({
    id: 'lvwjnEAjhvhHc1rJAVhi',
    defaultMessage: 'What is an eSIM?',
    description: 'Section heading for eSIM explanation in single-code details modal',
  })
  const whatIsEsimDescription = <FormattedMessage
    id="23KKJve4xO1GaIcV34W4"
    defaultMessage="Your free travel data is delivered via a digital eSIM — no SIM swap required. Keep your number and set up easily in the Hubby eSIM app. <link>Check if your phone supports eSIM</link>"
    description="Short eSIM explanation shown in single-code details modal with link to Hubby compatibility page"
    values={{
      link: (chunks: React.ReactNode) => <TextLink href={ESIM_HUBBY_COMPATIBLE_PHONES_LINK} target="_blank" rel="noopener noreferrer">{chunks}</TextLink>,
    }}
  />
  const howItWorksHeading = intl.formatMessage({
    id: 'cae5D_Bh1ZDaeaFVsnrg',
    defaultMessage: 'How it works',
    description: 'Section heading for how eSIM works in details modal',
  })
  const howToRedeemHeading = intl.formatMessage({
    id: 'zCWSU3OL0s-Bpl72aWPq',
    defaultMessage: 'How to redeem',
    description: 'Section heading for the eSIM benefit redemption steps in modal',
  })
  const step1Label = intl.formatMessage({
    id: 'Wit8rMli9-8qEodl8HTm',
    defaultMessage: 'Download the Hubby app by scanning the QR code or tapping the link',
    description: 'Step 1 instruction for redeeming eSIM benefit in the details modal',
  })
  const step3Label = intl.formatMessage({
    id: 'IA1VolCCp7RRBNh7c2ml',
    defaultMessage: 'Select your destination eSIM',
    description: 'Step 3 instruction for redeeming eSIM benefit in the details modal',
  })
  const step4Label = intl.formatMessage({
    id: 'OqH4wukRt2v2-L-mAKTL',
    defaultMessage: 'Install and activate when in your destination',
    description: 'Step 4 instruction for redeeming eSIM benefit in the details modal',
  })
  const generatingCodeLabel = intl.formatMessage({
    id: 'b8EqNTS0psu0tdvwtB4o',
    defaultMessage: 'Generating your code, this may take up to 5 minutes…',
    description: 'Message shown while single eSIM promo code is being generated',
  })
  const codeLoadErrorLabel = intl.formatMessage({
    id: 'oKIty-ovzL3IH40vIXVP',
    defaultMessage: 'Unable to load promo code. Please try again later.',
    description: 'Error message displayed when the eSIM promo code fails to load',
  })
  const qrCodeAlt = intl.formatMessage({
    id: 'Hvoe_jXus4j9_w2VF5BI',
    defaultMessage: 'QR code to download Hubby app',
    description: 'Alt text for the Hubby eSIM app download QR code image in modal',
  })
  const downloadHubbyLabel = intl.formatMessage({
    id: 'joTibS3cUFQ5R8_KwTyX',
    defaultMessage: 'Download Hubby app',
    description: 'Label text beneath the QR code for downloading the Hubby eSIM app',
  })
  const appStoreLabel = intl.formatMessage({
    id: 'w8U-6flvRQQvhtUpXk2m',
    defaultMessage: 'Download on the App Store',
    description: 'Accessibility label for the Apple App Store download link button',
  })
  const appStoreAlt = intl.formatMessage({
    id: '_R36jYNRTqXk7---yayM',
    defaultMessage: 'Download on the App Store',
    description: 'Alt text for the Apple App Store download badge image in modal',
  })
  const playStoreLabel = intl.formatMessage({
    id: 'G_xbV5DgwkrKATfOq_wi',
    defaultMessage: 'Get it on Google Play',
    description: 'Accessibility label for the Google Play Store download link button',
  })
  const playStoreAlt = intl.formatMessage({
    id: 'Haz37j4wffF8QGsHO-0q',
    defaultMessage: 'Get it on Google Play',
    description: 'Alt text for the Google Play Store download badge image in modal',
  })
  const promoCodesHeading = intl.formatMessage({
    id: 'T08DACyQB-ax_n6xI2NK',
    defaultMessage: 'Your promo codes',
    description: 'Section heading for the eSIM promo codes list in the modal',
  })
  const noCodesMessage = intl.formatMessage({
    id: 'ukcz8512TzO2ip7ythY2',
    defaultMessage: 'Book an international trip to unlock your complimentary travel eSIM and access your promo code here.',
    description: 'Message shown when the user has no eligible eSIM promo codes available',
  })

  const singleCodeCopiedEndIcon = (
    <CopiedFeedback>
      <BodyText variant="small" colour="light-foreground-alt">{copiedLabel}</BodyText>
      <LineCheckIcon size="S" />
    </CopiedFeedback>
  )

  return (
    <ModalContent>
      <Group direction="vertical" gap={24}>
        {title}
        <BodyText variant="medium" colour="light-foreground-alt">{introDescription}</BodyText>

        <Group direction="vertical" gap={4}>
          {hasSingleCode && <>
            <Heading variant="heading5">{whatIsEsimHeading}</Heading>
            <BodyText variant="medium">{whatIsEsimDescription}</BodyText>
          </>}
          {!hasSingleCode && <>
            <Heading variant="heading5">{howItWorksHeading}</Heading>
            <BodyText variant="medium">
              <FormattedMessage
                id="GvNnDAsVsHKzowA4fcYq"
                defaultMessage="Your free travel data is delivered via a digital eSIM — no SIM swap required. Keep your number and set up easily in the Hubby app. <link>Check if your phone supports eSIM</link>"
                description="Description of how the eSIM benefit works with link to Hubby compatibility page"
                values={{
                  link: (chunks: React.ReactNode) => <TextLink href={ESIM_HUBBY_COMPATIBLE_PHONES_LINK} target="_blank" rel="noopener noreferrer">{chunks}</TextLink>,
                }}
              />
            </BodyText>
          </>}
        </Group>

        <ContentStableMinHeight>
          <RedeemSection>
            <StepsColumn>
              <Heading variant="heading5">{howToRedeemHeading}</Heading>
              <StepsList>
                <StepItem>
                  <StepNumber>{intl.formatNumber(1)}</StepNumber>
                  <BodyText variant="medium">{step1Label}</BodyText>
                </StepItem>
                <StepItem>
                  <StepNumber>{intl.formatNumber(2)}</StepNumber>
                  <BodyText variant="medium">
                    {hasSingleCode && <FormattedMessage
                      id="N6frQJOrnGk5LtCY2MNY"
                      defaultMessage="Enter the promo code associated with your international booking. You can find it below or in <link>My Escapes.</link>"
                      description="Step 2 for redeeming single eSIM benefit with link to My Escapes"
                      values={{
                        link: (chunks: React.ReactNode) => <TextLink to="/account/my-escapes">{chunks}</TextLink>,
                      }}
                    />}
                    {!hasSingleCode && <FormattedMessage
                      id="YGhzykp6P2gLQ8_NPE_e"
                      defaultMessage="Enter the promo code associated with your international booking. You can find them below or in <link>My Escapes.</link>"
                      description="Step 2 for redeeming multiple eSIM benefits with link to My Escapes"
                      values={{
                        link: (chunks: React.ReactNode) => <TextLink to="/account/my-escapes">{chunks}</TextLink>,
                      }}
                    />}
                  </BodyText>
                </StepItem>

                {hasSingleCode && isBnblWithoutEsimDates && <SingleCodeAvailableLaterBox>
                  <InfoIconWrap>
                    <LineInfoCircleIcon size="S" />
                  </InfoIconWrap>
                  <BodyText variant="medium">
                    {intl.formatMessage({
                      id: 'OelPVia4UIF5MK6GQBFP',
                      defaultMessage: 'Choose your travel dates to unlock your code, available 7 days before departure.',
                      description: 'Message when BNBL booking has no travel dates yet for eSIM promo code reveal',
                    })}
                  </BodyText>
                </SingleCodeAvailableLaterBox>}

                {(showOrderPromoCode && singleCode?.phase === 'loading') && <SingleCodeSkeletonWrap>
                  <LoadingBox height={44} borderRadius="S" style={{ width: '100%' }} />
                </SingleCodeSkeletonWrap>}
                {showOrderPromoCode && singleCode?.phase === 'generating' && <SingleCodeGeneratingBox>
                  <LoadingBox height={44} borderRadius="S" style={{ width: '100%' }} />
                  <BodyText variant="small" colour="light-foreground-alt">{generatingCodeLabel}</BodyText>
                </SingleCodeGeneratingBox>}
                {isEligibleListReady && showOrderPromoCode && singleCode?.phase === 'success' && singleCode.code && <SingleCodeItemWrap>
                  <SingleCodeItem
                    title={singleCode.code}
                    titleLineClamp={1}
                    size="medium"
                    endIcon={showCopiedTooltip ? singleCodeCopiedEndIcon : <LineCopyIcon size="S" />}
                    onClick={handleSingleCodeCopy}
                  />
                </SingleCodeItemWrap>}
                {showOrderPromoCode && orderPhase === 'booking_pending' && <SingleCodeGeneratingBox>
                  <LoadingBox height={44} borderRadius="S" style={{ width: '100%' }} />
                  <BodyText variant="small" colour="light-foreground-alt">{generatingCodeLabel}</BodyText>
                </SingleCodeGeneratingBox>}

                {showOrderPromoCode && singleCode?.phase === 'available_later' && singleCode.availableDate && <SingleCodeAvailableLaterBox>
                  <InfoIconWrap>
                    <LineInfoCircleIcon size="S" />
                  </InfoIconWrap>
                  <BodyText variant="medium">
                    {intl.formatMessage({
                      id: 'MsC8HFQ21wV1__fljMUf',
                      defaultMessage: 'Code available on {date}',
                      description: 'Message when single eSIM promo code is not yet within reveal window',
                    }, { date: singleCode.availableDate })}
                  </BodyText>
                </SingleCodeAvailableLaterBox>}

                {showOrderPromoCode && singleCode?.phase === 'error' && <MessageBanner
                  kind="critical"
                  icon={<LineInfoCircleIcon />}
                >
                  {codeLoadErrorLabel}
                </MessageBanner>}
                {showOrderPromoCode && !singleCode && orderPhase === 'error' && <MessageBanner
                  kind="critical"
                  icon={<LineInfoCircleIcon />}
                >
                  {codeLoadErrorLabel}
                </MessageBanner>}
                {showOrderPromoCode && !singleCode && !orderPhase && <SingleCodeSkeletonWrap>
                  <LoadingBox height={44} borderRadius="S" style={{ width: '100%' }} />
                </SingleCodeSkeletonWrap>}

                <StepItem>
                  <StepNumber>{intl.formatNumber(3)}</StepNumber>
                  <BodyText variant="medium">{step3Label}</BodyText>
                </StepItem>
                <StepItem>
                  <StepNumber>{intl.formatNumber(4)}</StepNumber>
                  <BodyText variant="medium">{step4Label}</BodyText>
                </StepItem>
              </StepsList>
            </StepsColumn>
            <QrColumn>
              <QrImageWrap>
                <ResponsiveImage
                  id={HUBBY_QR_IMAGE_ID}
                  alt={qrCodeAlt}
                  width={104}
                  height={104}
                  objectFit="contain"
                />
              </QrImageWrap>
              <BodyText variant="medium">{downloadHubbyLabel}</BodyText>
              <AppBadgesRow>
                <ClickableLink
                  href={HUBBY_APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={appStoreLabel}
                >
                  <Image
                    id="ojt6s93jsh33gi4i8rwh"
                    alt={appStoreAlt}
                    width={120}
                  />
                </ClickableLink>
                <ClickableLink
                  href={HUBBY_PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={playStoreLabel}
                >
                  <Image
                    id="8k5pubb1rmb5xwvx0sh"
                    alt={playStoreAlt}
                    width={120}
                  />
                </ClickableLink>
              </AppBadgesRow>
            </QrColumn>
          </RedeemSection>

          {(!isEligibleListReady || hasNoCodes || hasMultipleCodes) && <PromoCodesSectionContent>
            <Heading variant="heading5">{promoCodesHeading}</Heading>

            {!isEligibleListReady && <PromoCodesGrid>
              <LoadingBox height={120} borderRadius="S" style={{ width: '100%' }} />
              <LoadingBox height={120} borderRadius="S" style={{ width: '100%' }} />
            </PromoCodesGrid>}

            {isEligibleListReady && hasNoCodes && <NoCodesInfoBox>
              <InfoIconWrap>
                <LineInfoCircleIcon />
              </InfoIconWrap>
              <NoCodesInfoText variant="medium">{noCodesMessage}</NoCodesInfoText>
            </NoCodesInfoBox>}

            {isEligibleListReady && hasMultipleCodes && <PromoCodesGrid>
              {codes.map(entry => <LoyaltyEsimDetailsPromoCodeCard
                key={entry.orderId}
                destination={entry.destination}
                dates={entry.dates}
                phase={entry.phase}
                code={entry.code}
                availableDate={entry.availableDate}
                orderId={entry.orderId}
                onViewOrder={onViewOrder}
                tier={entry.tier}
                dataAllowanceGb={entry.packageSizeGB ?? undefined}
              />)}
            </PromoCodesGrid>}
          </PromoCodesSectionContent>}
        </ContentStableMinHeight>
      </Group>
    </ModalContent>
  )
}

export default LoyaltyEsimDetailsContent
